import { Controller } from "@hotwired/stimulus"
import { base64urlToBuffer, bufferToBase64url, getCsrfToken } from "../utils/webauthn"

export default class extends Controller {
  static targets = ["button", "error"]
  static values = {
    challengeUrl: String,
    loginUrl: String
  }

  connect() {
    if (!window.PublicKeyCredential) {
      this.buttonTarget.classList.add("d-none")
    }
  }

  async authenticate(event) {
    event.preventDefault()
    const button = this.buttonTarget
    button.disabled = true

    try {
      const challengeResponse = await fetch(this.challengeUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": getCsrfToken()
        }
      })

      if (!challengeResponse.ok) throw new Error("Failed to get challenge")

      const options = await challengeResponse.json()

      options.challenge = base64urlToBuffer(options.challenge)
      if (options.allowCredentials) {
        options.allowCredentials = options.allowCredentials.map((cred) => ({
          ...cred,
          id: base64urlToBuffer(cred.id)
        }))
      }

      const credential = await navigator.credentials.get({ publicKey: options })

      const loginResponse = await fetch(this.loginUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": getCsrfToken()
        },
        body: JSON.stringify({
          credential: {
            id: credential.id,
            rawId: bufferToBase64url(credential.rawId),
            type: credential.type,
            response: {
              authenticatorData: bufferToBase64url(credential.response.authenticatorData),
              clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
              signature: bufferToBase64url(credential.response.signature),
              userHandle: credential.response.userHandle ? bufferToBase64url(credential.response.userHandle) : null
            }
          }
        })
      })

      if (!loginResponse.ok) {
        this.showError()
        return
      }

      const data = await loginResponse.json()
      const safeRedirect = data.redirect_to?.startsWith("/") && !data.redirect_to?.startsWith("//")
      window.location.href = safeRedirect ? data.redirect_to : "/"
    } catch (error) {
      if (error.name !== "NotAllowedError") {
        console.error("Passkey login error:", error)
        this.showError()
      }
    } finally {
      button.disabled = false
    }
  }

  showError() {
    if (this.hasErrorTarget) {
      this.errorTarget.classList.remove("d-none")
    }
  }
}
